/**
 * ======================================================
 * CUSTOM SELECT COMPONENT
 * ======================================================
 *
 * A fully accessible, mobile-friendly select dropdown component
 * with keyboard navigation and ARIA support.
 *
 * ------------------------------------------------------
 * FEATURES
 * ------------------------------------------------------
 * - Keyboard accessible (Arrow keys, Enter, Escape)
 * - Mobile-friendly with overlay
 * - ARIA attributes for screen readers
 * - Custom events for integration
 * - Header visibility management (when used with fixed headers)
 *
 * ------------------------------------------------------
 * BASIC USAGE
 * ------------------------------------------------------
 * <custom-select class="sort-by">
 *   <button class="options__trigger" aria-expanded="false">
 *     <span class="options__value">Default Sorting</span>
 *   </button>
 *   <div class="options__drawer">
 *    <div class="options__body">
 *     <div class="options__item" value="manual" aria-selected="true">Featured</div>
 *     <div class="options__item" value="price-ascending">Price: Low to High</div>
 *     <div class="options__item" value="price-descending">Price: High to Low</div>
 *    </div>
 *   </div>
 * </custom-select>
 *
 * ------------------------------------------------------
 * CUSTOMIZATION GUIDE
 * ------------------------------------------------------
 * 1. To change animation/transition:
 *    - Modify CSS for .options__drawer
 *    - Add/remove transition properties
 *
 * 2. To change mobile behavior:
 *    - Adjust the overlay handling in open()/close()
 *    - Modify the 999px breakpoint if needed
 *
 * 3. To integrate with fixed/transparent headers:
 *    - Set isFixed/isTransparentEnabled flags
 *    - Implement lockHeaderVisibility/unlockHeaderVisibility
 *    - Implement defaultHeader/initializeHeader functions
 *
 * 4. To remove mobile overlay:
 *    - Delete overlay creation code in constructor
 *    - Remove overlay event listeners
 *
 * 5. To change keyboard behavior:
 *    - Modify handleArrowKeys() method
 *    - Adjust key listeners in connectedCallback()
 *
 * ------------------------------------------------------
 * EVENT API
 * ------------------------------------------------------
 * Dispatches 'change' event with:
 * - detail.value: The value attribute of selected option
 * - detail.text: The text content of selected option
 *
 * Example:
 * document.querySelector('custom-select').addEventListener('change', (e) => {
 *   console.log('Selected:', e.detail.value, e.detail.text);
 * });
 *
 * ------------------------------------------------------
 * ACCESSIBILITY FEATURES
 * ------------------------------------------------------
 * - aria-expanded on trigger button
 * - aria-selected on options
 * - Keyboard navigation (arrows, Enter, Escape)
 * - Focus management
 * - Screen reader friendly
 */
class CustomSelect extends HTMLElement {
  constructor() {
    super();

    // State management
    this.isOpen = false;

    // DOM references
    this.trigger = this.querySelector(".options__trigger");
    this.drawer = this.querySelector(".options__drawer");
    this.closeButton = this.querySelector(".options__close");
    this.options = this.querySelectorAll(".options__item");
    this.currentValue = this.querySelector(".options__value");
    this.handleClickOutside = this.handleClickOutside.bind(this);

    // Create overlay for mobile
    this.overlay = document.createElement("div");
    this.overlay.className = "options__overlay";
    this.insertBefore(this.overlay, this.drawer);
  }

  // Sets up all event listeners
  connectedCallback() {
    document.addEventListener("click", this.handleClickOutside);

    // Toggle on trigger click
    this.trigger.addEventListener("click", () => this.toggle());

    // Close on overlay click
    this.overlay.addEventListener("click", () => this.close());

    // Close button if present
    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => this.close());
    }

    // Set up option selection
    this.options.forEach((option) => {
      option.addEventListener("click", () => this.selectOption(option));
      option.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.selectOption(option);
        }
      });
    });

    // Close on Escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    });

    // Handle keyboard navigation
    this.drawer.addEventListener("keydown", (e) => {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        this.handleArrowKeys(e);
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleClickOutside);
  }

  handleClickOutside(e) {
    if (this.isOpen && !this.contains(e.target)) {
      this.close();
    }
  }

  // Toggles the dropdown open/closed
  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  /**
   * Opens the dropdown
   * - Sets ARIA attributes
   * - Handles mobile overlay
   * - Manages header visibility if needed
   */
  open() {
    this.isOpen = true;
    this.setAttribute("open", "");
    this.trigger.setAttribute("aria-expanded", "true");

    // Mobile handling
    // if (window.innerWidth < 999) {
    //   document.body.style.overflow = "hidden";
    // }

    // Header management
    // if (this.isFixed) this.lockHeaderVisibility();
    // if (this.isTransparentEnabled && !this.isFixed) {
    //   this.defaultHeader();
    //   this.removeHeaderListeners();
    // }

    // Focus first option when opening
    // setTimeout(() => {
    //   const firstOption = this.querySelector(".options__item");
    //   if (firstOption) firstOption.focus();
    // }, 100);
  }

  /**
   * Closes the dropdown
   * - Resets ARIA attributes
   * - Cleans up mobile overlay
   * - Restores header behavior
   */
  close() {
    this.isOpen = false;
    this.removeAttribute("open");
    this.trigger.setAttribute("aria-expanded", "false");
    this.trigger.focus();
    // document.body.style.overflow = "";

    // // Header management
    // if (isFixed) unlockHeaderVisibility();
    // if (isTransparentEnabled && !isFixed) {
    //   initializeHeader(window.scrollY);
    //   initializeHeaderEvents();
    // }
  }

  /**
   * Handles option selection
   * @param {HTMLElement} option - The selected option element
   */
  selectOption(option) {
    if (option.hasAttribute("disabled")) return;

    const value = option.getAttribute("value");
    const text = option.textContent.trim();

    // Update selected value display
    this.currentValue.textContent = text;

    // Update aria-selected states
    this.options.forEach((opt) => {
      opt.setAttribute("aria-selected", opt === option ? "true" : "false");
    });

    // Close the drawer
    this.close();

    /**
     * Dispatches change event
     * @param {string} value - The value of selected option
     * @param {string} text - The text of selected option
     */
    this.dispatchEvent(
      new CustomEvent("change", {
        detail: { value, text },
      })
    );

    // You could also submit a form or navigate here if needed
    // window.location.href = window.location.pathname + '?sort_by=' + value;
  }

  handleArrowKeys(e) {
    const current = document.activeElement;
    const options = Array.from(this.options);
    const currentIndex = options.indexOf(current);

    if (e.key === "ArrowDown") {
      const nextIndex = (currentIndex + 1) % options.length;
      options[nextIndex].focus();
    } else if (e.key === "ArrowUp") {
      const prevIndex = (currentIndex - 1 + options.length) % options.length;
      options[prevIndex].focus();
    } else if (e.key === "Escape") {
      this.close();
    }
  }
}

customElements.define("custom-select", CustomSelect);
